import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  useDispatch,
  // useSelector
} from 'react-redux';
import { Row, Col, Image } from 'react-bootstrap';
// import { toast } from 'react-toastify';
// import Rating from '../components/Rating';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Meta from '../components/Meta';
import PresaleCountdown from '../components/PresaleCountdown';
import {
  useGetArtworkDetailsQuery,
  useGetPresaleArtworkQuery,
  // useCreateReviewMutation,
} from '../slices/artworksApiSlice';
import { addToCart } from '../slices/cartSlice';
import { sizeMap } from '../utils/sizeHelpers';
import { linkHelpers } from '../utils';

const ArtworkScreen = () => {
  const { id: artworkId, slug } = useParams();
  const isPresaleRoute = Boolean(slug);
  const detailsQuery = useGetArtworkDetailsQuery(artworkId, {
    skip: isPresaleRoute,
  });
  const presaleQuery = useGetPresaleArtworkQuery(slug, {
    skip: !isPresaleRoute,
  });
  const {
    data: artwork,
    isLoading,
    // refetch,
    error,
  } = isPresaleRoute ? presaleQuery : detailsQuery;
  const [qty, setQty] = useState(1);
  // const [rating, setRating] = useState(0);
  // const [comment, setComment] = useState('');
  const [price, setPrice] = useState(0);
  const [size, setSize] = useState('');

  useEffect(() => {
    if (!isLoading && artwork) {
      const basePrice = Object.values(artwork.price[0])[0];
      setSize(artwork.size[0]);
      setPrice(basePrice);
    }
  }, [artwork, isLoading]);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  // Once the presale window closes, hand over to the normal sale page
  useEffect(() => {
    if (
      isPresaleRoute &&
      artwork &&
      (!artwork.presale || new Date(artwork.presale.endDate) <= new Date())
    ) {
      navigate(`/artwork/${artwork._id}`, { replace: true });
    }
  }, [isPresaleRoute, artwork, navigate]);

  // const [createReview, { isLoading: loadingArtworkReview }] =
  //   useCreateReviewMutation();
  // const { userInfo } = useSelector((state) => state.auth);

  const addToCartHandler = () => {
    dispatch(
      addToCart({ ...artwork, qty, selectedPrice: price, selectedSize: size }),
    );
    navigate('/cart');
  };

  // const submitHandler = async (e) => {
  //   e.preventDefault();

  //   try {
  //     await createReview({
  //       artworkId,
  //       rating,
  //       comment,
  //     }).unwrap();
  //     refetch();
  //     toast.success('Review Submitted');
  //     setRating(0);
  //     setComment('');
  //   } catch (error) {
  //     toast.error(error?.data?.message || error.error);
  //   }
  // };

  if (isLoading) return;
  if (error)
    return (
      <Message variant="danger">{error?.data?.message || error.error}</Message>
    );
  const { buildArtworkLink } = linkHelpers;
  const artLink = buildArtworkLink(artwork.image);

  const isSoldOut = artwork.countInStock === 0;
  // Map each size key to its price, then find the biggest size (highest price)
  const sizePriceMap = artwork.price.reduce((acc, obj) => {
    for (const key in obj) {
      if (key !== '_id') acc[key] = obj[key];
    }
    return acc;
  }, {});
  const maxPrice = Math.max(...Object.values(sizePriceMap));
  const biggestSizeKey = Object.keys(sizePriceMap).find(
    (key) => sizePriceMap[key] === maxPrice,
  );

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || error.error}
        </Message>
      ) : (
        <>
          <Meta
            title={`${artwork.name} | Limbism`}
            description={`${artwork.medium}. Edition of ${artwork.editionNumber}.${artwork.about ? ' ' + artwork.about : ''} Limited edition art print by Limbism.`}
            keywords={`Limbism, ${artwork.name}, limited edition print, pop art, buy art, digital art, art collector`}
            image={artLink}
          />
          {isPresaleRoute && artwork.presale && (
            <div className="presale-header">
              <h1 className="presale-title">Presale Open</h1>
              <div className="presale-subtitle">Presale ends in</div>
              <PresaleCountdown
                endDate={artwork.presale.endDate}
                onComplete={() =>
                  navigate(`/artwork/${artwork._id}`, { replace: true })
                }
              />
            </div>
          )}
          <Row className="align-items-start">
            <Col md={7}>
              <div className="aw-image-wrap">
                <Image
                  src={artLink}
                  alt={artwork.name}
                  fluid
                  className="aw-image"
                />
                {isSoldOut && <span className="sold-out-banner">Sold Out</span>}
              </div>
            </Col>

            <Col md={5}>
              <div className="aw-details">
                <div className="aw-eyebrow">
                  Limited Edition · Edition of {artwork.editionNumber}
                </div>
                <h1 className="aw-title">{artwork.name}</h1>
                {artwork.medium && (
                  <div className="aw-medium-block">
                    <span className="aw-label">Medium</span>
                    <p className="aw-medium">{artwork.medium}</p>
                  </div>
                )}

                {artwork.description && (
                  <div className="aw-section">
                    <div className="aw-section-title">Description</div>
                    <p className="aw-section-body">{artwork.description}</p>
                  </div>
                )}

                <div className="aw-release aw-section">
                  Released {artwork.releaseDate}
                </div>

                {isSoldOut ? (
                  <>
                    <span className="sold-out-badge">Sold Out</span>
                    <div className="aw-soldout-meta">
                      <div>
                        <span className="aw-label">Size</span>
                        <span className="aw-meta-value">
                          {sizeMap[biggestSizeKey]}
                        </span>
                      </div>
                      <div>
                        <span className="aw-label">Price</span>
                        <span className="aw-price-sm">${maxPrice}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="aw-panel">
                    <div className="aw-price">${sizePriceMap[size]}</div>

                    <div className="aw-field">
                      <span className="aw-label">Size</span>
                      {artwork.size.length === 1 ? (
                        <div className="aw-size-single">{sizeMap[size]}</div>
                      ) : (
                        <div className="aw-sizes">
                          {artwork.size.map((s) => (
                            <button
                              key={s}
                              type="button"
                              className={`aw-size-pill${
                                s === size ? ' active' : ''
                              }`}
                              onClick={() => {
                                setSize(s);
                                setPrice(sizePriceMap[s]);
                              }}
                            >
                              {sizeMap[s]}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {(() => {
                      const pct =
                        (artwork.countInStock / artwork.editionNumber) * 100;
                      if (pct > 50) return null;
                      const color = pct > 20 ? '#f0ad4e' : '#e53935';
                      return (
                        <div className="aw-avail">
                          <span className="aw-label">Availability</span>
                          <div className="aw-avail-track">
                            <div
                              className="aw-avail-fill"
                              style={{ width: `${pct}%`, background: color }}
                            />
                          </div>
                          <div className="aw-avail-text">
                            {artwork.countInStock} of {artwork.editionNumber}{' '}
                            available
                          </div>
                        </div>
                      );
                    })()}

                    <div className="aw-field aw-field-inline">
                      <span className="aw-label">Quantity</span>
                      <select
                        className="aw-qty"
                        value={qty}
                        onChange={(e) => setQty(Number(e.target.value))}
                      >
                        {[...Array(artwork.countInStock).keys()].map((key) => (
                          <option key={key + 1} value={key + 1}>
                            {key + 1}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      className="aw-btn"
                      onClick={addToCartHandler}
                    >
                      Add to Cart
                    </button>
                  </div>
                )}
                <Link className="aw-back" to="/">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Back to gallery
                </Link>
              </div>
            </Col>
          </Row>
          {/* TODO:  uncomment if reviews are introduced again */}
          {/* <Row className="review">
            <Col md={6}>
              <h2>Reviews</h2>
              {artwork.reviews.length === 0 && <Message>No Reviews</Message>}
              <ListGroup variant="flush">
                {artwork.reviews.map((review) => (
                  <ListGroup.Item key={review._id}>
                    <strong>
                      <Rating value={review.rating}></Rating>
                      <p>{review.createdAt.substring(0, 10)}</p>
                      <p>{review.comment}</p>
                    </strong>
                  </ListGroup.Item>
                ))}
                <ListGroup.Item>
                  <h2>Write a Collector Review</h2>
                  {loadingArtworkReview && <Loader />}
                  {userInfo ? (
                    <Form onSubmit={submitHandler}>
                      <Form.Group contorlId="rating" className="my-w">
                        <Form.Label>Rating</Form.Label>
                        {/* Adjust rating here */}
          {/* <Form.Control
                          as="select"
                          value={rating}
                          onChange={(e) => setRating(e.target.value)}
                        >
                          <option value="...">Select</option>
                          <option value="1">1 - Poor</option>
                          <option value="2">2 - Fair</option>
                          <option value="3">3 - Good</option>
                          <option value="4">4 - Very Good</option>
                          <option value="5">5 - Excellent</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group contorlId="comment" className="my-2">
                        <Form.Label>Comment</Form.Label>
                        <Form.Control
                          as="textarea"
                          row="3"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                        ></Form.Control>
                      </Form.Group>
                      <Button
                        disabled={loadingArtworkReview}
                        type="submit"
                        variant="primary"
                      >
                        Submit
                      </Button>
                    </Form>
                  ) : (
                    <Message>
                      Please <Link to="/login">sign in</Link> to write a review
                    </Message>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row> */}
        </>
      )}
    </>
  );
};

export default ArtworkScreen;
